import "src/styles/globals.css";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import ThemeComponent from "src/@core/theme/ThemeComponent";
import {
  SettingsConsumer,
  SettingsProvider,
} from "src/@core/context/settingsContext";
import AppLayout from "src/layouts/AppLayout";
import { AuthProvider } from "src/context/AuthContext";
import { AxiosInterceptor } from "src/configs/axios";
import "react-perfect-scrollbar/dist/css/styles.css";
import AuthGuard from "src/components/Auth/AuthGuard";
import GuestGuard from "src/components/Auth/GuestGuard";
import Spinner from "src/@core/components/spinner";
import NProgress from "nprogress";
import { Router, useRouter } from "next/router";
import ErrorBoundary from "src/components/ErrorBoundary";
import Head from "next/head";
import { queryClient } from "src/configs/reactQuery";

const clientSideEmotionCache = createCache({
  key: "css",
  stylisPlugins: [prefixer, rtlPlugin],
});

// const queryClient = new QueryClient({
//   defaultOptions: { queries: { refetchOnWindowFocus: false } },
// });

const Guard = ({ children, authGuard }) => {
  if (authGuard) {
    return <AuthGuard fallback={<Spinner />}>{children}</AuthGuard>;
  } else {
    return <GuestGuard fallback={<Spinner />}>{children}</GuestGuard>;
  }
};

Router.events.on("routeChangeStart", () => {
  NProgress.start();
});
Router.events.on("routeChangeError", () => {
  NProgress.done();
});
Router.events.on("routeChangeComplete", () => {
  NProgress.done();
});

const defaultLandingPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: "https://www.mozono.ir/",
  name: "موزونو | خرید از آمازون در ایران بدون واسطه | خرید از آمازون امارات",
  alternateName: "موزونو |‌ خرید از آمازون",
  description:
    "موزونو |‌ خرید از آمازون ، پلتفرم خرید از آمازون امارات است که بهترین قیمت ، سریع ترین زمان تحویل ، تضمین کیفیت و اصل بودن کالا را به شما ارایه می کند هدف ما تسهیل خرید از آمازون به بهترین قیمت است.",
};

// For the organization details:
const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "موزونو | خرید از آمازون در ایران بدون واسطه | خرید از آمازون امارات",
  url: "https://www.mozono.ir",
  logo: "https://www.mozono.com/favicon.ico",
  sameAs: [
    // Your social profiles
    "https://www.linkedin.com/company/mozono/",
    "https://t.me/+-hhFXt4qvWIxYjhk",
    "https://instagram.com/mozono.ir?igshid=YmMyMTA2M2Y=",
    //... other social profiles
  ],
};

const defaulOpenGraphs = [
  {
    property: "og:title",
    content:
      "موزونو | خرید از آمازون در ایران بدون واسطه | خرید از آمازون امارات",
  },
  { property: "og:type", content: "website" },
  { property: "og:image", content: "https://mozono.ir/favicon.ico" },
  { property: "og:url", content: "https://mozono.ir" },
  {
    property: "og:description",
    content:
      "موزونو |‌ خرید از آمازون ، مطمئن‌ترین و آسان‌ترین پلتفرم خرید از آمازون. به‌صرفه و با تضمین اصالت و سلامت کالا. ارسال  در سریع‌ترین زمان.",
  },
  {
    property: "og:site_name",
    content:
      "موزونو | خرید از آمازون در ایران بدون واسطه | خرید از آمازون امارات",
  },
];

export default function App(props) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const router = useRouter();

  const authGuard = Component.authGuard ?? false;
  const pageTitle =
    pageProps.title ||
    "موزونو | خرید از آمازون در ایران بدون واسطه | خرید از آمازون امارات";
  const pageDescription =
    pageProps.description ||
    "موزونو |‌ خرید از آمازون ، مطمئن‌ترین و آسان‌ترین پلتفرم خرید از آمازون. به‌صرفه و با تضمین اصالت و سلامت کالا. ارسال  در سریع‌ترین زمان.";

  const openGraphsData = pageProps.openGraphsData || defaulOpenGraphs;
  const reviewSchema = pageProps.reviewsSchema || null;
  const landingPageJsonLd =
    pageProps.landingPageJsonLd || defaultLandingPageJsonLd;

  const noIndexRoutes = [
    "/profile",
    "/accounts",
    "/checkout",
    "/orders",
    "/payemnt-result",
    "/tickets",
  ];
  const shouldNoIndex = noIndexRoutes.includes(router.pathname);
  const isTestEnv = process.env.NEXT_PUBLIC_ENV === "development";

  // const [queryClient] = useState(() => new QueryClient())

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="preload"
          href="/fonts/IRANYekanRegular.ttf"
          as="font"
          type="font/ttf"
          crossorigin
        />
        <link rel="stylesheet" href="/font.css" />

        <link rel="icon" href="/favicon.ico" />
        <link rel="shortcut icon" href="/favicon.ico" />

        <meta
          name="keywords"
          content="موزونو |‌ خرید از آمازون , آمازون, امارات, خرید, تخفیف, دبی"
        />

        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1"
        />

        {/* <link
          href="https://cdn.jsdelivr.net/gh/rastikerdar/vazirmatn@v33.003/Vazirmatn-font-face.css"
          rel="stylesheet"
          type="text/css"
        /> */}

        <link rel="canonical" href="https://mozono.ir/" />

        {/* <!-- This CSP allows only scripts loaded from the current site's origin --> */}
        {/* <meta
          http-equiv="Content-Security-Policy"
          content="default-src 'self';"
        /> */}
        {isTestEnv ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          shouldNoIndex && <meta name="robots" content="noindex, nofollow" />
        )}

        {/* Open Graphs tags */}
        {openGraphsData.map((og) => (
          <meta key={og.property || og.name} {...og} />
        ))}

        {reviewSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewSchema) }}
          />
        )}

        {/* Google Analytics meta Tags */}
        {/* <Script
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=G-JSC28L9G3C`}
        />
        <Script strategy="afterInteractive">
          {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', 'G-JSC28L9G3C');
       `}
        </Script> */}

        <script type="application/ld+json">
          {JSON.stringify(landingPageJsonLd)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
      </Head>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AxiosInterceptor>
            <Hydrate state={pageProps.dehydratedState}>
              <CacheProvider value={emotionCache}>
                <SettingsProvider {...{}}>
                  <SettingsConsumer>
                    {({ settings }) => {
                      return (
                        <ThemeComponent settings={settings}>
                          <Guard authGuard={authGuard}>
                            <AppLayout>
                              <ErrorBoundary>
                                <Component {...pageProps} />
                              </ErrorBoundary>
                            </AppLayout>
                          </Guard>
                        </ThemeComponent>
                      );
                    }}
                  </SettingsConsumer>
                </SettingsProvider>
              </CacheProvider>
            </Hydrate>
          </AxiosInterceptor>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}
